<template>
  <div
    class="field"
    data-test="select-project"
  >
    <label
      v-if="label && is_dropdown"
      class="mb-2 block font-bold"
    >
      {{ label }}
    </label>

    <div v-if="is_dropdown">
      <AutoComplete
        ref="autocomplete"
        v-model="project_name"
        :suggestions="filteredProjects"
        optionLabel="attributes.name"
        field="attributes.name"
        :appendTo="append_to_body ? 'body' : undefined"
        :showClear="true"
        completeOnFocus
        :pt="{
          pcInputText: {
            root: { 'data-test': 'select-project-input' },
          },
        }"
        inputClass="w-full"
        class="h-9 w-full"
        @complete="onAutoSearch"
        @item-select="selectProject"
        @option-select="selectProject"
        @clear="clearProject"
        @update:modelValue="onProjectNameUpdate"
      >
        <template #empty>
          <div data-test="empty-result">
            <div v-if="is_searching">
              {{ $t('select.actions.noResults') }}
              <span v-if="project_name && typeof project_name === 'string'">"{{ project_name }}"</span>
            </div>
            <div v-else>
              {{ $t('select.actions.startTyping') }}
            </div>
          </div>
        </template>
      </AutoComplete>
    </div>

    <InputText
      v-else
      ref="inputRef"
      v-model="project_name"
      class="w-full"
      :placeholder="$t('project.labels.searchProject')"
      @update:modelValue="searchProject"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useToasts } from '@/app/util/toasts';
import _ from 'lodash-es';
import AutoComplete from 'primevue/autocomplete';
import InputText from 'primevue/inputtext';

const inputRef = ref(null);

const props = defineProps({
  active: { type: Object, default: null },
  append_to_body: { type: Boolean, default: false },
  label: { type: String, default: null },
  projects_props: { type: Object, default: null },
  is_dropdown: { type: Boolean, default: true },
  autofocus: { type: Boolean, default: false },
});

const emit = defineEmits(['onSearch', 'on-search', 'changeSelect', 'change-select']);

const store = useStore();
const toast_action_failed = useToasts().toast_action_failed;

const project_name = ref(props.active ? props.active.attributes.name : '');
const is_searching = ref(false);
const projects_search = ref({});

onMounted(() => {
  if (props.autofocus) {
    if (inputRef.value) inputRef.value.$el.focus();
  }
});

const projects = computed(() => {
  if (props.projects_props) {
    return props.projects_props;
  }
  return projects_search.value;
});

const filteredProjects = computed(() => {
  const query = typeof project_name.value === 'string' ? project_name.value.toLowerCase() : '';

  return _.filter(projects.value, (p) => p.attributes.name.toLowerCase().indexOf(query) >= 0);
});

watch(
  () => props.active,
  (newVal) => {
    project_name.value = _.get(newVal, 'attributes.name', '');
  },
);

const onProjectNameUpdate = (value) => {
  if (value && typeof value === 'object') {
    emit('changeSelect', value);
    emit('change-select', value);
  }
};

const autocompleteProjects = _.debounce(async (search = '') => {
  try {
    const results = await store.dispatch('AUTOCOMPLETE_PROJECTS', {
      name_cont: search,
    });
    projects_search.value = _.keyBy(results, 'id');
  } catch (error) {
    toast_action_failed();
  }
}, 100);

const onAutoSearch = (event) => {
  searchProject(event.query);
};

const searchProject = (query) => {
  if (!query) {
    is_searching.value = false;
    emit('onSearch', '');
    emit('on-search', '');
    return;
  }

  if (query.length >= 1) {
    is_searching.value = true;
    autocompleteProjects(query);
  } else {
    is_searching.value = false;
  }
  emit('onSearch', query);
  emit('on-search', query);
};

const selectProject = (event) => {
  const selected = event && event.value ? event.value : event;

  emit('changeSelect', selected);
  emit('change-select', selected);
};

const clearProject = () => {
  selectProject(null);
  project_name.value = '';
};
</script>
